import {
  Box,
  Dialog,
  DialogTitle,
  Typography,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Paper,
  Divider,
} from "@mui/material";
import React, { useState, useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { UrlApi } from "../../../../../services/apirest";

function HabilitarChofer(props) {
  const { alta, onClose, sucursal, choferesHabilitados } = props;
  const [open, setOpen] = useState(alta);
  const [choferesDisponibles, setChoferesDisponibles] = useState([]);
  const [selectedChoferes, setSelectedChoferes] = useState([]);
  const [tempSelectedChofer, setTempSelectedChofer] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  
  // Asegurar que choferesHabilitados sea un array
  const habilitadosArray = Array.isArray(choferesHabilitados) ? choferesHabilitados : [];
  
  // Obtener IDs de choferes ya habilitados (usando id_chofer en lugar de id)
  const habilitadosIds = habilitadosArray.map(ch => ch.id_chofer || ch.id) || [];

  // Obtener IDs de choferes ya seleccionados temporalmente
  const selectedIds = selectedChoferes.map(ch => ch.id);

  // Filtrar choferes disponibles (excluyendo ya habilitados y ya seleccionados)
  const availableChoferes = Array.isArray(choferesDisponibles) 
    ? choferesDisponibles.filter(chofer => 
        !habilitadosIds.includes(chofer.id) && !selectedIds.includes(chofer.id)
      )
    : [];

  // Función para obtener choferes disponibles
  const getChoferesDisponibles = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${UrlApi}/choferes/envasadoxsucursal/${sucursal.id}`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const dataArray = Array.isArray(data) ? data : [];
      setChoferesDisponibles(dataArray);
    } catch (error) {
      console.error("Error al obtener choferes:", error);
      setChoferesDisponibles([]);
      setSnackbar({
        open: true,
        message: "Error al cargar los choferes: " + error.message,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Función para agregar chofer a la lista
  const handleAddChofer = () => {
    if (tempSelectedChofer) {
      setSelectedChoferes([...selectedChoferes, tempSelectedChofer]);
      setTempSelectedChofer(null);
    }
  };

  // Función para eliminar chofer de la lista
  const handleRemoveChofer = (choferToRemove) => {
    setSelectedChoferes(selectedChoferes.filter(chofer => chofer.id !== choferToRemove.id));
  };

  // Función para habilitar choferes (POST)
  const postHabilitarChoferes = async (data) => {
  try {
    const response = await fetch(`${UrlApi}/envasado_rendiciones_extendidas/multiple`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Error al habilitar choferes:', error);
    throw error;
  }
};

  useEffect(() => {
    if (open && sucursal?.id) {
      getChoferesDisponibles();
    }
  }, [open, sucursal]);

  const handleClose = () => {
    setOpen(false);
    setSelectedChoferes([]);
    setTempSelectedChofer(null);
    if (onClose) onClose();
  };

  const handleSubmit = async () => {
    if (selectedChoferes.length === 0) {
      setSnackbar({
        open: true,
        message: "Debe seleccionar al menos un chofer",
        severity: "warning",
      });
      return;
    }

    setSubmitting(true);

    const dataToSend = selectedChoferes.map(chofer => ({
      id_chofer: chofer.id, // El chofer tiene id, no id_chofer
      activo: 1,
    }));


    try {
      await postHabilitarChoferes(dataToSend);
      
      setSnackbar({
        open: true,
        message: `${selectedChoferes.length} chofer(es) habilitado(s) correctamente`,
        severity: "success",
      });
      
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Error al habilitar choferes: " + error.message,
        severity: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <>
      <Dialog fullWidth open={open} onClose={handleClose} maxWidth="md">
        <Box justifyContent="center" backgroundColor="#1F618D">
          <DialogTitle>
            <Typography fontSize={20} align="center" color="#ffffff">
              Habilitar Rendiciones Extendidas
            </Typography>
          </DialogTitle>
        </Box>
        
        <DialogContent dividers>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Seleccione choferes para habilitar las rendiciones extendidas:
              </Typography>
              
              {/* Autocomplete para buscar y seleccionar choferes */}
              <Box display="flex" gap={1} alignItems="center" mb={3}>
                <Autocomplete
                  sx={{ flex: 1 }}
                  options={availableChoferes}
                  getOptionLabel={(option) => {
                    if (!option) return '';
                    return `${option.nombre_apellido || ''} ${option.dni ? `(DNI: ${option.dni})` : ''}${option.legajo ? ` - Legajo: ${option.legajo}` : ''}`;
                  }}
                  value={tempSelectedChofer}
                  onChange={(event, newValue) => {
                    setTempSelectedChofer(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Buscar chofer"
                      placeholder="Escriba para buscar..."
                      size="small"
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option?.id === value?.id}
                  noOptionsText="No hay choferes disponibles"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddChofer}
                  disabled={!tempSelectedChofer}
                  startIcon={<PersonAddIcon />}
                >
                  Agregar
                </Button>
              </Box>

              {/* Lista de choferes seleccionados */}
              {selectedChoferes.length > 0 && (
                <Paper variant="outlined" sx={{ mt: 2, mb: 2 }}>
                  <Box sx={{ p: 2, backgroundColor: "#f5f5f5", borderBottom: "1px solid #e0e0e0" }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Choferes seleccionados ({selectedChoferes.length})
                    </Typography>
                  </Box>
                  <List dense>
                    {selectedChoferes.map((chofer, index) => (
                      <React.Fragment key={chofer.id}>
                        <ListItem
                          secondaryAction={
                            <IconButton 
                              edge="end" 
                              aria-label="delete"
                              onClick={() => handleRemoveChofer(chofer)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          }
                        >
                          <ListItemText
                            primary={chofer.nombre_apellido}
                            secondary={
                              <>
                                {chofer.dni && `DNI: ${chofer.dni}`}
                                {chofer.legajo && ` | Legajo: ${chofer.legajo}`}
                                {chofer.sucursal && ` | Sucursal: ${chofer.sucursal}`}
                              </>
                            }
                          />
                        </ListItem>
                        {index < selectedChoferes.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </Paper>
              )}

              {availableChoferes.length === 0 && selectedChoferes.length === 0 && !loading && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  No hay choferes disponibles para habilitar. Todos los choferes ya tienen rendiciones extendidas activas.
                </Alert>
              )}

              {availableChoferes.length === 0 && selectedChoferes.length > 0 && !loading && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  Ha seleccionado {selectedChoferes.length} chofer(es). Presione "Habilitar" para confirmar.
                </Alert>
              )}
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 2, justifyContent: "space-between" }}>
          <Button onClick={handleClose} color="secondary" variant="outlined">
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={selectedChoferes.length === 0 || submitting || loading}
            startIcon={submitting && <CircularProgress size={20} />}
          >
            {submitting ? "Habilitando..." : `Habilitar (${selectedChoferes.length})`}
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default HabilitarChofer;